import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { loadIcon } from 'iconify-icon';
import { catchError, of, Observable, first, map, concatMap, combineLatest } from 'rxjs';
import { CATEGORIES } from '@libs/constants/game-cats.constants';
import { addIcon } from '@libs/store/icons/actions';
import { selectIcons } from '@libs/store/icons/selectors';
@Injectable({
  providedIn: 'root',
})
export class IconsService {
  constructor(
    private http: HttpClient,
    private store: Store,
  ) {}

  /**
   * Initializes the IconsService by preloading icons.
   * @returns {Observable<any>} An observable that completes when the icons are preloaded.
   */
  init() {
    return this.preloadIcons().pipe(first());
  }

  /**
   * Loads an icon by name.
   * @param {string} name - The name of the icon to load.
   * @returns {Observable<any>} An observable containing the loaded icon.
   */
  load(name: string) {
    return this.find(name).pipe(concatMap((icon: any) => (icon ? of(icon) : this.loadIfNoAdded(name))));
  }

  /**
   * Loads an icon if it is not already added.
   * @param {string} name - The name of the icon to load.
   * @returns {Observable<any>} An observable containing the loaded icon or null if not found.
   */
  private loadIfNoAdded(name: string) {
    return this.loadIcon(name).pipe(
      catchError(() => this.fromAssets(name)),
      map((data: any | string) => (data ? this.add(data, name) : null)),
    );
  }

  /**
   * Loads an icon from assets.
   * @param {string} name - The name of the icon to load.
   * @returns {Observable<string | null>} An observable containing the icon data or null if not found.
   */
  private fromAssets(name: string) {
    if (!name) {
      return of(null);
    }
    return this.http
      .get(`assets/i/${name.toLowerCase()}.svg`, { responseType: 'text' })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Loads an icon using the iconify library.
   * @param {string} name - The name of the icon to load.
   * @returns {Observable<string>} An observable containing the loaded icon data.
   */
  private loadIcon(name: string) {
    return new Observable(observer => {
      loadIcon(name)
        .then(data =>
          observer.next(
            `<svg viewBox="0 0 ${data?.width} ${data?.height}"  xmlns="http://www.w3.org/2000/svg">${data?.body}</svg>`,
          ),
        )
        .catch(() => {
          observer.error(null);
        });
    });
  }

  /**
   * Preloads a list of icons.
   * @returns {Observable<any[]>} An observable containing the preloaded icons.
   */
  private preloadIcons() {
    const officeIcons = Office_icons;
    const gamesIcons = Games_icons;
    const icons = [...Apps_icons_names, ...officeIcons, ...gamesIcons];
    return combineLatest(icons.map(name => this.loadIfNoAdded(name)));
  }

  /**
   * Adds an icon to the store.
   * @param {string} data - The icon data.
   * @param {string} name - The name of the icon.
   * @returns {string} The icon data.
   */
  private add(data: string, name: string): string {
    this.store.dispatch(addIcon({ name, data }));
    return data;
  }

  /**
   * Finds an icon by name in the store.
   * @param {string} name - The name of the icon to find.
   * @returns {Observable<string>} An observable containing the icon data.
   */
  private find(name: string): Observable<string> {
    return this.store.select(selectIcons).pipe(
      map(icons => icons?.[name]),
      first(),
    );
  }
}
export const Games_icons = CATEGORIES.map(cat => cat.icon);
export const Office_icons = ['card-withdraw', 'card-deposit', 'bicho', ...Games_icons];
export const Apps_icons_names = [
  'arrow-left',
  'arrow-right',
  'arrow-down',
  'arrow-up',
  'star',
  'time',
  'shield',
  'cassino',
  'casino',
  'esports',
  'burger',
  'home',
  'person',
  'wallet',
  'chat',
  'ears',
  'brl',
  'eth',
  'btc',
  'xrp',
  'ltc',
  'bnb',
  'close',
  'usdt',
  'gaming',
  'settings',
  'rewards',
  'promotion',
  'transactions-accordion',
  'game-history',
  'card-deposit',
  'add-items',
  'esports',
  'shield-checked',
  'camera',
  'docs',
  'verified',
  'iphone',
  'create-box',
  'search',
];
